/** Compliance-stage rich rendering — HF EU prompt schema + Path C additive. */
import { Shield, ShieldCheck, ShieldAlert, ShieldX, Globe2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ConfidenceBadge } from './ConfidenceBadge';
import {
  PathCKbCitations,
  PathCRuleEvaluations,
} from './RichCompliancePathC';
import { AiLawList, type AiLaw } from './AiLawList';

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asText = (v: unknown): string => (typeof v === 'string' ? v : '');
const asBool = (v: unknown): boolean => Boolean(v);
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};

const SEVERITY_CLS: Record<string, string> = {
  LOW: 'bg-green-50 text-green-700 border-green-200',
  MEDIUM: 'bg-amber-50 text-amber-800 border-amber-200',
  HIGH: 'bg-orange-50 text-orange-800 border-orange-300',
  CRITICAL: 'bg-red-50 text-red-800 border-red-300',
};

const RISK_LEVEL_CLS: Record<string, string> = {
  COMPLIANT: 'bg-green-50 text-green-700 border-green-200',
  LOW_RISK: 'bg-green-50 text-green-700 border-green-200',
  MEDIUM_RISK: 'bg-amber-50 text-amber-800 border-amber-200',
  HIGH_RISK: 'bg-orange-50 text-orange-800 border-orange-300',
  NON_COMPLIANT: 'bg-red-50 text-red-800 border-red-300',
};

const VERDICT_CLS: Record<string, string> = {
  COMPLIANT: 'bg-green-50 text-green-700 border-green-200',
  MINOR_ISSUES: 'bg-amber-50 text-amber-800 border-amber-200',
  SIGNIFICANT_ISSUES: 'bg-orange-50 text-orange-800 border-orange-300',
  NON_COMPLIANT: 'bg-red-50 text-red-800 border-red-300',
};

function YesNo({ label, value }: { label: string; value: unknown }) {
  const tone = value === true
    ? 'text-green-700 dark:text-green-400'
    : value === false
      ? 'text-red-700 dark:text-red-400'
      : 'text-muted-foreground';
  const display = value === true ? 'yes' : value === false ? 'no' : '—';
  return (
    <div className="flex justify-between gap-3 text-xs border-b py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium ${tone}`}>{display}</span>
    </div>
  );
}

function IssueRow({ issue }: { issue: Record<string, unknown> }) {
  const sev = asText(issue.severity) || 'MEDIUM';
  const sevCls = SEVERITY_CLS[sev] ?? '';
  return (
    <li className="rounded border px-2 py-1.5 space-y-0.5 text-xs">
      <div className="flex items-start gap-2">
        <Badge variant="outline" className={`text-[10px] shrink-0 ${sevCls}`}>{sev}</Badge>
        <span className="text-muted-foreground">{asText(issue.issue_id) || '—'}</span>
        {asText(issue.relevant_gdpr_article) && (
          <span className="text-muted-foreground">· {asText(issue.relevant_gdpr_article)}</span>
        )}
        {asText(issue.issue_type) && (
          <span className="text-muted-foreground">· {asText(issue.issue_type)}</span>
        )}
        {asText(issue.related_clause_id) && (
          <span className="text-muted-foreground">· {asText(issue.related_clause_id)}</span>
        )}
      </div>
      {asText(issue.description) && <p>{asText(issue.description)}</p>}
      {asText(issue.recommendation) && (
        <p className="text-muted-foreground italic">→ {asText(issue.recommendation)}</p>
      )}
    </li>
  );
}

export function RichComplianceBody({ output }: { output: Record<string, unknown> }) {
  const gdpr = obj(output.gdpr_analysis);
  const privacy = obj(output.data_privacy_analysis);
  const jurisdiction = obj(output.jurisdiction_compliance);
  const issues = asArray(output.contractual_compliance_issues);
  const missing = asArray(output.missing_compliance_clauses);
  const verdict = asText(output.overall_compliance_verdict) || 'MINOR_ISSUES';
  const verdictCls = VERDICT_CLS[verdict] ?? '';
  const score = output.overall_compliance_score;
  const confidence = output.compliance_confidence_score;
  const summary = asText(output.compliance_summary);
  const flags = asArray(output.flags_for_downstream).map((f) => (typeof f === 'string' ? f : ''));
  const notes = asText(output.extraction_notes);
  const gdprIssues = asArray(gdpr.gdpr_issues);
  const gdprRisk = asText(gdpr.gdpr_risk_level) || 'LOW_RISK';
  const gdprRiskCls = RISK_LEVEL_CLS[gdprRisk] ?? '';
  const sensitive = asArray(privacy.sensitive_data_categories_mentioned).map(
    (x) => (typeof x === 'string' ? x : ''),
  );
  const frameworks = asArray(privacy.other_privacy_frameworks).map(
    (x) => (typeof x === 'string' ? x : ''),
  );
  const jurIssues = asArray(jurisdiction.jurisdiction_issues);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={`text-xs ${verdictCls}`}>{verdict}</Badge>
        {typeof score === 'number' && (
          <Badge variant="outline" className="text-[10px]">Score: {score}/100</Badge>
        )}
        <span className="ml-auto">
          <ConfidenceBadge score={confidence} rationale={notes} />
        </span>
      </div>

      {summary && (
        <div className="rounded border bg-muted/30 p-3 text-sm">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Compliance summary
          </p>
          <p>{summary}</p>
        </div>
      )}

      {/* GDPR */}
      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 space-y-2">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium">GDPR analysis</span>
            <Badge variant="outline" className={`text-[10px] ml-auto ${gdprRiskCls}`}>
              {gdprRisk}
            </Badge>
          </div>
          {asBool(gdpr.applicable) ? (
            <div className="grid grid-cols-2 gap-x-6">
              <YesNo label="Lawful basis stated" value={gdpr.lawful_basis_stated} />
              <YesNo label="DPA present" value={gdpr.data_processing_agreement_present} />
              <YesNo label="Data subjects identified" value={gdpr.data_subjects_identified} />
              <YesNo label="Retention period stated" value={gdpr.retention_period_stated} />
              <YesNo label="Right to erasure" value={gdpr.right_to_erasure_mentioned} />
              <YesNo label="Breach notification clause" value={gdpr.data_breach_notification_clause} />
              <YesNo label="DPO mentioned" value={gdpr.dpo_mentioned} />
              <div className="flex justify-between gap-3 text-xs border-b py-0.5">
                <span className="text-muted-foreground">Data transfer mechanism</span>
                <span className="font-medium">{asText(gdpr.data_transfer_mechanism) || '—'}</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">GDPR not applicable to this contract.</p>
          )}
          {gdprIssues.length > 0 && (
            <ul className="space-y-1">
              {gdprIssues.map((i, idx) => <IssueRow key={idx} issue={obj(i)} />)}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Data privacy */}
      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 space-y-1">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium">Data privacy</span>
          </div>
          <div className="grid grid-cols-2 gap-x-6">
            <YesNo label="Personal data mentioned" value={privacy.personal_data_mentioned} />
            <YesNo label="Privacy policy referenced" value={privacy.privacy_policy_referenced} />
            <YesNo label="CCPA applicable" value={privacy.ccpa_applicable} />
          </div>
          {sensitive.length > 0 && sensitive[0] !== 'none' && (
            <p className="text-xs">
              <span className="text-muted-foreground">Sensitive categories: </span>
              {sensitive.join(', ')}
            </p>
          )}
          {frameworks.length > 0 && (
            <p className="text-xs">
              <span className="text-muted-foreground">Other frameworks: </span>
              {frameworks.join(', ')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Jurisdiction */}
      <Card className="border-border">
        <CardContent className="pt-3 pb-3 px-4 space-y-2">
          <div className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium">Jurisdiction compliance</span>
          </div>
          <div className="text-xs">
            <span className="text-muted-foreground">Governing law: </span>
            <span className="font-medium">{asText(jurisdiction.governing_law) || '—'}</span>
          </div>
          {asText(jurisdiction.cross_border_risks) && (
            <p className="text-xs">
              <span className="text-muted-foreground">Cross-border risks: </span>
              {asText(jurisdiction.cross_border_risks)}
            </p>
          )}
          {jurIssues.length > 0 && (
            <ul className="space-y-1">
              {jurIssues.map((i, idx) => <IssueRow key={idx} issue={obj(i)} />)}
            </ul>
          )}
        </CardContent>
      </Card>

      {issues.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
            Contractual compliance issues ({issues.length})
          </p>
          <ul className="space-y-1">
            {issues.map((i, idx) => <IssueRow key={idx} issue={obj(i)} />)}
          </ul>
        </div>
      )}

      {missing.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/30">
          <p className="font-medium text-amber-800 dark:text-amber-300 flex items-center gap-1 mb-1">
            <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
            Missing compliance clauses ({missing.length})
          </p>
          <ul className="space-y-0.5 text-xs">
            {missing.map((m, i) => {
              const o = obj(m);
              const sev = asText(o.severity) || 'MEDIUM';
              return (
                <li key={i} className="flex items-start gap-2">
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${SEVERITY_CLS[sev] ?? ''}`}>
                    {sev}
                  </Badge>
                  <div>
                    <span className="font-medium">{asText(o.clause_type) || '—'}</span>
                    {asText(o.applicable_regulation) && (
                      <span className="text-muted-foreground"> — {asText(o.applicable_regulation)}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {flags.map((f, i) => (
            <span
              key={i}
              className="text-[10px] rounded bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300 px-1.5 py-0.5"
            >
              <ShieldX className="inline h-2.5 w-2.5 mr-0.5" aria-hidden="true" />
              {f}
            </span>
          ))}
        </div>
      )}

      {/* The laws this agent identified, shown on the stage that produced them —
          a reviewer approving compliance should see what it cited. */}
      <AiLawList laws={asArray(output.applicable_legislation) as AiLaw[]} corroborated={false} />

      <PathCRuleEvaluations output={output} />
      <PathCKbCitations output={output} />

      {notes && (
        <p className="text-xs italic text-muted-foreground border-l-2 pl-2">
          Extraction notes: {notes}
        </p>
      )}
    </div>
  );
}
